import React, { useState, useEffect, useCallback, useRef }  from 'react';
// import { useTranslation } from "react-i18next";
import { Row, Col } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { motion } from "framer-motion";
// import "./index.scss";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

import SkeletonLoader from "../../../../../ui/SkeletonLoader";
import NoDataFound from "../../../../../ui/NoDataFound";

import { trendingDownIcon, trendingUpIcon, downArrowIcon } from "../../../../../../utils/icons";

import { useSelector, useDispatch } from 'react-redux';
import { getVendorScoreChange, resetVendorScoreChange } from "../../../../../../store/actions/thirdPartyRiskAction";

const VendorScoreChange = ({customerId}) => {

    const vendorScoreChangeResponse = useSelector((state) => state.thirdPartyRisk.vendorScoreChange);
        
    const prevCustomerIdRef = useRef(null);
    
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("1M");
    const [showVendorScoreRange, setShowVendorScoreRange] = useState(false);

    const [vendorScoreChange, setVendorScoreChange] = useState(null);
    const [topPerformerList, setTopPerformrList] = useState(null);
    const [bottomPerformerList, setBottomPerferList] = useState(null);
    const [isLoading, setIsLoading] = useState(false)

    const formatCustomDate = (date, dateType) => {
        const pad = (num) => num.toString().padStart(2, '0');
      
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1); // Months are 0-indexed
        const day = pad(date.getDate());
        // const hours = pad(date.getHours());
        // const minutes = pad(date.getMinutes());
        // const seconds = pad(date.getSeconds());
      
        return `${year}-${month}-${day} ${dateType === "fromDate" ? "00:00:00" : "23:59:59"}`;
    };

    const getPastDate = (days) => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - days);
        return pastDate;
    };

    const fetchVendorScoreChange = useCallback(()=>{
        setIsLoading(true)        
        const pastDate = getPastDate(activeTab === "1M" ? 30 : activeTab === "2M" ? 60 : activeTab === "3M" ? 90 : 180)
        const todate = formatCustomDate(new Date(), "toDate")
        const fromDate =  formatCustomDate(pastDate, "fromDate")
        const url = `&customer_id=${customerId}&from_date=${fromDate}&to_date=${todate}`
        dispatch(getVendorScoreChange(url))
    },[customerId, activeTab, dispatch])

    useEffect(()=>{
        fetchVendorScoreChange()
    },[activeTab, fetchVendorScoreChange])

    useEffect(()=>{
        if(customerId !== prevCustomerIdRef.current) {
            fetchVendorScoreChange();
            prevCustomerIdRef.current = customerId;
        }
    },[customerId, fetchVendorScoreChange])

    useEffect(()=>{
        if(vendorScoreChangeResponse !== null) {
            setVendorScoreChange(vendorScoreChangeResponse)
            let topPerformer = vendorScoreChangeResponse?.stats?.organizations.filter((data)=>{
                return data?.stats?.overall_score?.trend === "increased"
            })

            setTopPerformrList(topPerformer)

            let bottomPerformer = vendorScoreChangeResponse?.stats?.organizations.filter((data)=>{
                return data?.stats?.overall_score?.trend === "decreased"
            })

            setBottomPerferList(bottomPerformer)
            setIsLoading(false)
            dispatch(resetVendorScoreChange())
        }
    },[vendorScoreChangeResponse, dispatch])

    useEffect(() => {
        if(vendorScoreChange !== null) {

            const list = vendorScoreChange?.stats?.organizations.map((data)=>{
                return {
                    name: data?.stats?.overall_score?.percentage+'%',
                    score: data?.stats?.overall_score?.obtained_score,
                    details: data
                }
            })
            const sortedList = list.sort((a, b) => a.name - b.name);
            
            let data = sortedList
            // Create root element
            let root = am5.Root.new("scorechartdiv");
        
            // Set themes
            root.setThemes([am5themes_Animated.new(root)]);
        
            // Create chart
            let chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: true,
                    panY: true,
                    wheelX: "none", // Disable mouse wheel zoom
                    wheelY: "none",
                    pinchZoomX: false, // Disable pinch zoom on X-axis
                    pinchZoomY: false, // Disable pinch zoom on Y-axis
                })
            );
        
            chart.get("colors").set("step", 2);
        
            // Disable "Created with amCharts 5" label
            root._logo.dispose();
        
            // Create axes
            let xRenderer = am5xy.AxisRendererX.new(root, {
                strokeOpacity: 0.2,
            });
        
            // Remove X-axis line
            xRenderer.set("visible", false);
        
            // Remove grid lines and ticks
            xRenderer.grid.template.set("visible", false);
            xRenderer.ticks.template.set("visible", false);

            xRenderer.labels.template.setAll({
                rotation: 0,
                multiLine: false,
                maxWidth: 100,
                paddingTop: 10,
                centerX: am5.p50,
                centerY: am5.p100,
                oversizedBehavior: "wrap"
            });

            xRenderer.set("minGridDistance", 1);

            xRenderer.labels.template.setAll({ fontSize: 14 });
        
            let xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "name",
                    renderer: xRenderer,
                    tooltip: null,
                })
            );
        
            let yRenderer = am5xy.AxisRendererY.new(root, {
                strokeOpacity: 0.2,
            });
        
            let yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    min: 0,
                    max: 950,
                    strictMinMax: true,
                    renderer: yRenderer,
                    calculateTotals: false,
                    numberFormat: "#a",
                    tooltip: null,
                })
            );

            // Force step of 100
            yAxis.setAll({
                interval: 100,
                extraMin: 0,
                extraMax: 0
            });

            yRenderer.setAll({
                minGridDistance: 20,
                visible: true,
                strokeOpacity: 0.1,
                inside: false
            });

            yRenderer.labels.template.setAll({
                multiLine: false,
                maxWidth: 100,
                paddingTop: 10,
                oversizedBehavior: "wrap",
                fontSize: 12,
                paddingRight: 10
            });

            yRenderer.set("visible", false);
            // Remove grid lines and ticks
            yRenderer.grid.template.set("visible", false);
            yRenderer.ticks.template.set("visible", false);
        
            // Add background ranges for Y-axis
            let range1 = yAxis.createAxisRange(
                yAxis.makeDataItem({
                    value: 0,
                    endValue: 250,
                })
            );
            range1.get("axisFill").setAll({
                fill: am5.color("#EA635D"),
                fillOpacity: 0.8,
                visible: true,
            });
        
            let range2 = yAxis.createAxisRange(
                yAxis.makeDataItem({
                    value: 250,
                    endValue: 500,
                })
            );
        
            range2.get("axisFill").setAll({
                fill: am5.color("#F0965A"),
                fillOpacity: 0.8,
                visible: true,
            });
        
            let range3 = yAxis.createAxisRange(
                yAxis.makeDataItem({
                    value: 500,
                    endValue: 800,
                })
            );
            range3.get("axisFill").setAll({
                fill: am5.color("#F4DA6B"),
                fillOpacity: 0.8,
                visible: true,
            });
        
            let range4 = yAxis.createAxisRange(
                yAxis.makeDataItem({
                    value: 800,
                    endValue: 1000,
                })
            );
            range4.get("axisFill").setAll({
                fill: am5.color("#94D86B"),
                fillOpacity: 0.8,
                visible: true,
            });
        
            // Create series
            let series = chart.series.push(
                am5xy.LineSeries.new(root, {
                    calculateAggregates: true,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: "score",
                    categoryXField: "name",
                    tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    html: `<div class="custom-tooltip-container">
                            <div class="tooltip-heading"><h2 class="m-0">{dataItem.dataContext.details.name}</h2></div>                            
                            <div class="align-items-center w-100">
                                <span class="tooltip-key d-inline-block">{valueY}/{dataItem.dataContext.details.stats.overall_score.max_score}</span>
                            </div>
                        </div>`, // Use your custom HTML
                    getFillFromSprite: false,
                    background: am5.PointedRectangle.new(root, {
                        fill: am5.color(0xffffff), // Fixed white background
                        fillOpacity: 1,           // Fully opaque
                        stroke: am5.color(0xFFFFFF), // Border color
                        strokeWidth: 1,           // Border width
                        pointerBaseWidth: 10,      // Reduce pointer base width
                        pointerLength: 8,         // Reduce pointer length to minimize space
                        cornerRadius: 10,          // Optional rounded corners
                        shadowColor: am5.color(0xeaf1ff), // Shadow color
                        shadowBlur: 40.9,                 // Amount of blur for the shadow
                        shadowOffsetX: 5,               // Horizontal shadow offset
                        shadowOffsetY: 5,
                        height: 108
                    }),
                }),
                })
            );
        
            series.set("stroke", am5.color("#062465"))
        
            // Add circle bullet
            series.bullets.push(function () {
                let graphics = am5.Circle.new(root, {
                    strokeWidth: 2,
                    radius: 3,
                    stroke: series.get("stroke"),
                    fill: am5.color("#062465"),
                });
                return am5.Bullet.new(root, {
                    sprite: graphics,
                });
            });
        
            // Add cursor
            chart.set(
                "cursor",
                am5xy.XYCursor.new(root, {
                    xAxis: xAxis,
                    yAxis: yAxis,
                    snapToSeries: [series],
                    behavior: "none", // Prevent zooming
                })
            );
            
            if (data.length > 0) {
                series.data.setAll(data);
                xAxis.data.setAll(data);
            }
        
            // Make stuff animate on load
            series.appear(1000);
            chart.appear(1000, 100);            
        
            // Cleanup on unmount
            return () => {
                root.dispose();
            };
        }
            
    },[vendorScoreChange]);

    return (
        <div className="bg-white-with-shadow p-4 risk-score-over-time-container mt-4">
            <div className={`vendor-score-container ${showVendorScoreRange ? "active" : ""}`}>
                <h4 className="heading-text mb-3">Vendor with Great Positive/Negative Score Change</h4>
                {
                    isLoading ?
                        <SkeletonLoader type={"line"} count={6} currentPage={"dashboardOverview"} />
                    :
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Row>
                            <Col lg={6}>
                                <div className="tab-container rounded d-flex">
                                    <p className={`rounded text-center ${activeTab === "1M" ? "active" : ""}`}
                                        onClick={() => setActiveTab("1M")}>1M</p>
                                    <p className={`rounded text-center ${activeTab === "2M" ? "active" : ""}`}
                                        onClick={() => setActiveTab("2M")}>2M</p>
                                    <p className={`rounded text-center ${activeTab === "3M" ? "active" : ""}`}
                                        onClick={() => setActiveTab("3M")}>3M</p>
                                    <p className={`rounded text-center ${activeTab === "6M" ? "active" : ""}`}
                                        onClick={() => setActiveTab("6M")}>6M</p>
                                </div>
                                <div className="mt-3">
                                    <div
                                        id="scorechartdiv"
                                        style={{
                                            width: "100%",
                                            height: "270px",
                                        }}
                                    >
                                    </div>
                                </div>
                            </Col>
                            <Col lg={6}>
                                <div className="performers-table d-flex gap-3 border rounded p-3 w-100">
                                    <div className="bottom-performer-table w-50">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Bottom Performers</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    bottomPerformerList?.length > 0 &&
                                                    bottomPerformerList?.map((data, index)=>{
                                                        return (
                                                            <tr key={index}>
                                                                <td>{data?.name}</td>
                                                                <td>{data?.stats?.overall_score?.percentage}{trendingDownIcon()}</td>
                                                            </tr>
                                                        )
                                                    })
                                                }                                            
                                            </tbody>
                                        </Table>
                                        {
                                            bottomPerformerList?.length === 0 &&
                                                <NoDataFound noDataText={"No Record Found"}/>
                                        }
                                    </div>
                                    <div className="top-performer-table w-50">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th>Top Performers</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    topPerformerList?.length > 0 &&
                                                    topPerformerList?.map((data, index)=>{
                                                        return (
                                                            <tr key={index}>
                                                                <td>{data?.name}</td>
                                                                <td>{data?.stats?.overall_score?.percentage}{trendingUpIcon()}</td>
                                                            </tr>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </Table>
                                        {
                                            topPerformerList?.length === 0 &&
                                                <NoDataFound noDataText={"No Record Found"}/>
                                        }
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </motion.div>
                }
                
            </div>
            <div className="p-2 text-center mt-2 close-vendor-section">
                <p className="text cursor-pointer" 
                    onClick={()=>setShowVendorScoreRange(!showVendorScoreRange)}>
                        {!showVendorScoreRange ? "View Vendor Score Change" : "Close Vendor Score Change"}
                        <span className={`down-arrow ${!showVendorScoreRange ? "active" : ""}`}>{downArrowIcon()}</span>
                </p>
            </div>
        </div>
    )
}

export default VendorScoreChange;